import { DefaultOptionType } from 'antd/lib/select';
import { useEffect, useMemo, useState } from 'react';
import { useQueryClient } from 'react-query';
import { useHistory, useLocation } from 'react-router-dom';
import { ROUTES } from 'shared/constants/ROUTES';
import { useFilter } from 'shared/hooks/useFilter';
import { usePagination } from 'shared/hooks/usePagination';
import { useSearch } from 'shared/hooks/useSearch';
import { useSelectOptions } from 'shared/hooks/useSelectOptions';
import { IChannel } from 'shared/interfaces/IChannel';
import { IProgram } from 'shared/interfaces/IProgram';
import { IProgramSetup } from 'shared/interfaces/IProgramSetup';
import { useChannelService } from 'shared/services/channelService';
import { useProgramService } from 'shared/services/programService';
import { useProgramSetupService } from 'shared/services/programSetupService';
import { Alert } from 'shared/theme/elements';

import ListView from './ListView';
import { useAVAutomationService } from 'shared/services/avAutomationService';
import { DeleteConfirmModal } from '../ProgramSetup/components/DeleteConfirmModal';

export interface ProgramSetupFilter {
  channel?: string;
  program?: string;
  station?: string;
}

export interface ProgramSetupSearch {
  program?: string;
  channel?: string;
  station?: string;
}

const filterDefaultValues: ProgramSetupFilter = {
  channel: undefined,
  program: undefined,
  station: undefined,
};

const searchDefaultValues: ProgramSetupSearch = {
  program: undefined,
  channel: undefined,
  station: undefined,
};

export interface ProgramSetupSelectOptions {
  // channel: DefaultOptionType[];
  program: DefaultOptionType[];
  station: DefaultOptionType[];
}

export interface ProgramSetupLoading {
  // channel?: boolean;
  program?: boolean;
  station?: boolean;
  programSetup?: boolean;
}

const ListContainer = () => {
  const queryClient = useQueryClient();
  const history = useHistory();
  const { state } = useLocation();
  const locationState = state as any;

  const pagination = usePagination({ key: 'program-setup' });
  const { page, pageSize, handlePageChange } = pagination;

  const { filter, setFilter } = useFilter<ProgramSetupFilter>(filterDefaultValues);
  const { search, setSearch } = useSearch<ProgramSetupSearch>(searchDefaultValues, {
    options: {
      // channel: {
      //   delay: 800,
      // },
      station: {
        delay: 800,
      },
      program: {
        delay: 800,
      },
    },
  });

  // Set default station filter to DZBB on mount
  useEffect(() => {
    if (!filter.station) {
      setFilter('station', '619f1734a834a290ce566c17');
    }
  }, [filter.station, setFilter]);

  /**
   * Channel Service
   */
  // const { getChannel } = useChannelService();
  // const { data: channel, isLoading: channelLoading } = getChannel({
  //   media_source_type_flag: ['video'],
  //   channel_name: search.channel,
  //   limit: 20,
  //   country: ['PHILIPPINES'],
  // });
  // const channelData = useMemo(() => channel?.data, [channel?.data]);
  // const { data: channelOptions } = useSelectOptions<IChannel>({
  //   data: channelData,
  //   labelKey: 'publisher_name',
  //   valueKey: 'publisher_obj_id',
  // });

  // new add
  /**
   * Station Service
   */
  const { getStation } = useChannelService();
  const { data: station, isLoading: stationLoading } = getStation({
    media_source_type_flag: ['audio'],
    channel_name: search.station,
    limit: 20,
    country: ['PHILIPPINES'],
  });
  const stationData = useMemo(() => station?.data, [station?.data]);
  const { data: stationOptions } = useSelectOptions<IChannel>({
    data: stationData,
    labelKey: 'publisher_name',
    valueKey: 'publisher_obj_id',
  });

  /**
   * Program Service
   */
  const { getProgram } = useProgramService();
  const { data: program, isLoading: programLoading } = getProgram({
    channel_id: filter.channel,
    program_name: search.program,
    media_source_type_flag: 'audio',
  });
  const programData = useMemo(() => program?.data, [program?.data]);
  const { data: programOptions } = useSelectOptions<IProgram>({
    data: programData,
    labelKey: 'program_name',
    valueKey: '_id',
  });

  /**
   * Audio Video Automation - Program Setup Service
   */
  const { getProgramSetup, deleteProgramSetup } = useProgramSetupService();
  const { data: programSetup, isLoading: setupLoading } = getProgramSetup({
    page: page,
    limit: pageSize,
    sort: '',
    // channel_obj_id: filter.channel,
    // program_obj_id: filter.program,
    station_program_obj_id: filter.program,
    station_obj_id: filter.station,
    machine_type: locationState?.machine_type,
    machine_ip: locationState?.machine_ip,
    audio_setup: 'yes',
  });
  // const programSetupData = useMemo(() => programSetup?.data?.[0], [programSetup?.data]);

  const programSetupOptions: ProgramSetupSelectOptions = useMemo(
    () => ({
      // channel: channelOptions,
      program: programOptions,
      station: stationOptions,
    }),
    [programOptions, stationOptions],
  );

  const programSetupLoading: ProgramSetupLoading = useMemo(
    () => ({
      // channel: channelLoading,
      program: programLoading,
      station: stationLoading,
      programSetup: setupLoading,
    }),
    [programLoading, setupLoading, stationLoading],
  );

  /**
   * Delete Program Setup
   * @param id program setup id
   * @returns axios promise
   */
  const deleteSetup = (id: string) => {
    const { deleteMutation } = deleteProgramSetup();

    return new Promise((resolve, reject) => {
      deleteMutation.mutateAsync(id, {
        onSuccess: () => {
          resolve(true);
          Alert.success('Program Setup Deleted!');
          queryClient.invalidateQueries('program_setup');
          queryClient.invalidateQueries('av_gaugemeter');
        },
        onError: (error: any) => {
          reject();
          Alert.error(error.response.data.server_response);
        },
      });
    });
  };

  /**
   * Handle on program setup list edit button
   * @param id Program Setup ID
   */
  const handleOnEdit = (id: string) => {
    history.push(ROUTES.RADIO.PROGRAMSETUP.EDIT, {
      id,
    });
  };

  /**
   * When delete button is clicked this opens the confirmation modal before actual delete
   * @param id Program Setup ID
   * @param station Selected Program Setup Channel (for display only)
   * @param program Selecte Program Setup Program (for display only)
   */
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; station: string; program: string } | null>(null);
  const [deleting, setDeleting] = useState(false);

  const handleOnDelete = (
    id: string,
    station: Pick<IProgramSetup, 'station_details'>['station_details'],
    program: Pick<IProgramSetup, 'station_program_details'>['station_program_details'],
  ) => {
    setDeleteTarget({
      id,
      station: station.station_name,
      program: program.station_program_name,
    });
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteSetup(deleteTarget.id);
    } catch {
      // Alert.error already surfaced the failure inside deleteSetup's onError.
    } finally {
      setDeleting(false);
      setDeleteTarget(null);
    }
  };

  const handleOnFilter = (key: keyof ProgramSetupFilter, value?: any) => {
    setFilter(key, value);
    handlePageChange(1);
  };

  useEffect(() => {
    queryClient.invalidateQueries('program_setup');
    queryClient.invalidateQueries('av_gaugemeter');
  }, [queryClient]);

  const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([]);
  const [selectedIP, setSelectedIP] = useState<string>('');

  const newSelectedRows: string[] = programSetup?.data?.map((r) => r?._id) || [];

  const handleSelect = (record: any, selected: string[]) => {
    if (selected) {
      setSelectedRowKeys((keys) => [...keys, record.key]);
    } else {
      setSelectedRowKeys((keys) => {
        const index = keys.indexOf(record.key);
        return [...keys.slice(0, index), ...keys.slice(index + 1)];
      });
    }
  };

  const toggleSelectAll = (rowsChecked: any) => {
    if (rowsChecked.target.checked) {
      setSelectedRowKeys((keys) => [...keys.filter((key) => !newSelectedRows.includes(key)), ...newSelectedRows]);
    } else {
      setSelectedRowKeys((keys) => keys.filter((key) => !newSelectedRows.includes(key)));
    }
  };

  const handleChangeIP = (value) => {
    setSelectedIP(value);
  };

  const { getGaugeMeter, transferMachine } = useAVAutomationService();
  const { transferMachineMutation } = transferMachine();

  const transferMachineIP = () => {
    return new Promise((resolve, reject) => {
      transferMachineMutation.mutateAsync(
        {
          av_setup_ids: selectedRowKeys,
          transfer_machine_ip: selectedIP,
          transfer_machine_type: locationState?.machine_type,
        },
        {
          onSuccess: () => {
            resolve(true);
            Alert.success('Programs Transfered');
            queryClient.invalidateQueries('program_setup');
            queryClient.invalidateQueries('av_gaugemeter');
            setSelectedIP('');
            setSelectedRowKeys([]);
          },
          onError: (error: any) => {
            reject();
            Alert.error(error.response.data.server_response);
          },
        },
      );
    });
  };

  const { data: gaugeData } = getGaugeMeter({
    machine_type: locationState?.machine_type ? [locationState?.machine_type] : undefined,
    limit: 100,
  });

  const IPOption = gaugeData?.data
    ?.filter((value) => (locationState?.machine_ip ? locationState?.machine_ip !== value.machine_ip : value))
    .map(({ machine_ip }) => ({ value: machine_ip, label: machine_ip }));

  useEffect(() => {
    if (selectedRowKeys.length === 0) {
      setSelectedIP('');
    }
  }, [selectedRowKeys]);

  return (
    <>
      <ListView
        data={programSetup}
        pagination={pagination}
        onDelete={handleOnDelete}
        onEdit={handleOnEdit}
        onFilter={handleOnFilter}
        filters={filter}
        search={search}
        onSearch={setSearch}
        options={programSetupOptions}
        loading={programSetupLoading}
        handleSelect={handleSelect}
        toggleSelectAll={toggleSelectAll}
        selectedRowKeys={selectedRowKeys}
        newSelectedRows={newSelectedRows}
        locationState={locationState}
        IPOption={IPOption}
        selectedIP={selectedIP}
        handleChangeIP={handleChangeIP}
        transferMachineIP={transferMachineIP}
      ></ListView>
      <DeleteConfirmModal
        open={!!deleteTarget}
        bodyText={
          deleteTarget
            ? `"${deleteTarget.program}" will be removed from ${deleteTarget.station}. This can't be undone.`
            : ''
        }
        loading={deleting}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={handleConfirmDelete}
      />
    </>
  );
};

export default ListContainer;
